<script setup lang="ts">
import InterviewContainer from '@/interview/components/instance-modal/InterviewContainer.vue';
import {computed, ref} from "vue";
import InterviewRecording from "@/interview/components/instance-modal/InterviewRecording.vue";
import {useInterviewStore} from "@/stores/interview-store";
import {storeToRefs} from "pinia";
import {useRoute, useRouter} from "vue-router";
import BaseButton from "@/core/components/base/BaseButton.vue";
import InterviewSurveyBuilt from "@/interview/components/instance-modal/InterviewSurveyBuilt.vue";
import {interviewApi} from "@/container";
import InterviewAudioPanel from "@/interview/components/instance-modal/InterviewAudioPanel.vue";
import InterviewDescription from "@/interview/components/instance-modal/InterviewDescription.vue";
import useInterviewAudio from "@/interview/composables/useInterviewAudio";

const interviewStore = useInterviewStore();
const {interview, prefilledSurveyAnswers, interviewInstance} = storeToRefs(interviewStore);
const router = useRouter();
const route = useRoute();

const {recordPlayerRef, startAudio, audioStarted, recordFinished, handleImport} = useInterviewAudio();

const userAnswerValues = ref<Record<number, unknown>>({});
const counterLoading = ref(false);

/**
 * Compute the answers.
 * If the user has answered a question, use that answer.
 * Otherwise, use the prefilled answer.
 */
const answers = computed(() => {
    return Object.keys(userAnswerValues.value)
        .reduce((acc, key) => {
            acc[key] = userAnswerValues.value[key].length
                ? userAnswerValues.value[key]
                : prefilledAnswers.value[key] ?? [];

            return acc;
        }, {});
});

/**
 * Compute the prefilled answers.
 */
const prefilledAnswers = computed(() => {
    return prefilledSurveyAnswers.value?.reduce((acc, answer) => {
        acc[answer.question_id] = answer.answer;

        return acc;
    }, {});
});

/**
 * Navigate to the transcript loading page.
 */
const navigateToTranscriptLoading = () => {
    router.replace({
        name: 'interview-instances.finished',
        query: {...route.query}
    });
};

/**
 * When counter interview is finished post guidelines answers.
 */
const onCounterInterviewFinished = async () => {
    if (audioStarted.value) {
        recordPlayerRef.value.finish();
    } else {
        counterLoading.value = true;
        try {
            await interviewStore.startInterviewInstance();

            if (interviewInstance.value) {
                const formattedAnswers = Object.keys(answers.value)
                    .map(key => {
                        return {
                            question_id: Number(key),
                            answer: answers.value[key]
                        };
                    }).filter((answer) => answer.answer.length > 0);
                const guidelineInstance = await interviewApi().answerGuidelines(interviewInstance.value.id, formattedAnswers);
                interviewInstance.value.guideline_survey_answer_instance_id = guidelineInstance.id;

                navigateToTranscriptLoading();
            }
        } catch (e) {
            console.error(e);
        } finally {
            counterLoading.value = false;
        }
    }
};

/**
 * Handle the change event.
 *
 * @param {Object} value The new value.
 * @param {number} questionId The question ID.
 */
const handleChange = ({value, questionId}) => {
    userAnswerValues.value[questionId] = Array.isArray(value) ? value : [value];
};
</script>

<template>
  <InterviewContainer class="ml-4 pt-0 px-0">
    <InterviewDescription
      v-if="interview"
      :description="interview.description"
      class="px-5"
      :duration="interview.duration"
      :video-url="interview.video_url"
    />
    <div class="px-5 pt-4">
      <InterviewRecording
        ref="recordPlayerRef"
        @start="startAudio"
        @finish="recordFinished"
        @finish-upload="handleImport"
      >
        <div>
          <h2 class="text-black">
            Déroulé de l'entretien
          </h2>
          <p>Suivez les consignes, ajoutez vos commentaires...</p>
        </div>
      </InterviewRecording>
      <InterviewAudioPanel />
      <div v-if="interview?.guideline_survey && interview.guideline_survey?.sections" class="flex flex-col gap-5 mt-3">
        <InterviewSurveyBuilt
          density="compact"
          :survey="interview.guideline_survey"
          :answers="answers"
          display-collapsible
          @change="handleChange"
        />
        <div class="flex justify-end">
          <BaseButton primary class="my-4" :loading="counterLoading" @click="onCounterInterviewFinished">
            Terminer l'entretien
          </BaseButton>
        </div>
      </div>
    </div>
  </InterviewContainer>
</template>
